import type { FieldSyncOutboxItem } from '@/mobile/sync/fieldSyncCache';
import type { FieldSyncResponse } from '@/mobile/sync/fieldSyncTypes';

export type FieldSyncPushResult = FieldSyncResponse['pushResults'][number];

export function fieldReportDayKey(
  projectId: number | string | undefined,
  reportDateIso: string | undefined,
): string | null {
  const project = Number(projectId);
  const date = reportDateIso?.trim();

  if (!project || !date) {
    return null;
  }

  return `${project}|${date}`;
}

export function outboxItemDayKey(item: FieldSyncOutboxItem): string | null {
  const payload = item.operation.payload;

  return fieldReportDayKey(
    payload.projectId as number | string,
    payload.reportDate as string,
  );
}

export function pushResultDayKey(result: FieldSyncPushResult): string | null {
  const report = result.report;

  if (!report) {
    return null;
  }

  return fieldReportDayKey(report.projectId, report.reportDateIso);
}

/** Servidor reutiliza o mesmo RDO/dia — o clientUuid do push pode ser outro. */
export function pushResultResolvesOutboxItem(
  result: FieldSyncPushResult,
  item: FieldSyncOutboxItem,
): boolean {
  if (result.status !== 'ok') {
    return false;
  }

  if (result.clientUuid === item.operation.clientUuid) {
    return true;
  }

  const resultKey = pushResultDayKey(result);
  const itemKey = outboxItemDayKey(item);

  return Boolean(resultKey && itemKey && resultKey === itemKey);
}

export function clientUuidsResolvedByPushResults(
  results: FieldSyncPushResult[],
  outbox: FieldSyncOutboxItem[],
): string[] {
  const uuids = new Set<string>();

  for (const result of results) {
    if (result.status !== 'ok') {
      continue;
    }

    if (result.clientUuid) {
      uuids.add(result.clientUuid);
    }

    for (const item of outbox) {
      if (pushResultResolvesOutboxItem(result, item)) {
        uuids.add(item.operation.clientUuid);
      }
    }
  }

  return Array.from(uuids);
}

export function wasSubmitSyncedByPushResults(
  results: FieldSyncPushResult[],
  clientUuid: string,
  projectId: number,
  reportDate: string,
): boolean {
  const dayKey = fieldReportDayKey(projectId, reportDate);

  return results.some((result) => {
    if (result.status !== 'ok') {
      return false;
    }

    if (result.clientUuid === clientUuid) {
      return true;
    }

    return Boolean(dayKey && pushResultDayKey(result) === dayKey);
  });
}
